import {
  firstError,
  MetricStat,
  unwrap,
  Widget,
  WidgetSkeleton,
  WidgetState,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { IP_BLOCKING_SERIES_CONTRACT } from "@nexus/widget-kit";
import type { IpBlockingSeriesData } from "@nexus/widget-kit";

export interface IpBlockingSummaryConfig {
  title?: string;
  mode?: "safety" | "volume" | "effectiveness";
}

export const manifest: WidgetManifest = {
  id: "ip-blocking-summary",
  name: "IP blocking summary",
  version: "1.0.0",
  description: "Creation-cohort volumes, conservative safety or ticket/ISP effectiveness.",
  dataRequests: {
    series: {
      source: "ip-blocking-effectiveness",
      contract: IP_BLOCKING_SERIES_CONTRACT,
      configurable: true,
      sources: [
        "ip-blocking-effectiveness",
        "ip-blocking-cohorts",
        "domain-blocking-effectiveness",
        "domain-blocking-cohorts",
      ],
    },
  },
};
export const defaultConfig: IpBlockingSummaryConfig = { mode: "safety" };
const count = (value: number | null | undefined) =>
  value == null ? "Unavailable" : new Intl.NumberFormat("en").format(value);
const percent = (value: number | null | undefined) =>
  value == null
    ? "Unavailable"
    : new Intl.NumberFormat("en", {
        style: "percent",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value);

export function Component({ data, config }: WidgetProps<IpBlockingSummaryConfig>) {
  const report = unwrap<IpBlockingSeriesData>(data.series);
  const error = firstError(data, ["series"]);
  const mode = config.mode ?? "safety";
  const safety = report?.safety;
  const sending = report?.effectiveness?.total;
  const cohort = report?.cohorts?.total;
  const targets = report?.targetKind === "DOMAIN" ? "domains" : "IPs";
  const targetLabel = report?.targetKind === "DOMAIN" ? "Domain" : "IP";
  const heading =
    mode === "safety"
      ? "Safe to block"
      : mode === "volume"
        ? `Detected and sent ${targets}`
        : "Blocking effectiveness";
  return (
    <Widget title={config.title?.trim() || heading} intrinsicHeight>
      {data.series?.isLoading && !report ? (
        <WidgetSkeleton shape="kpi" />
      ) : error ? (
        <WidgetIssueState error={error} />
      ) : !report ? (
        <WidgetState title={`No ${targetLabel.toLowerCase()} blocking data`} />
      ) : (
        <div className="density-metric-padding min-w-0 space-y-3">
          {mode === "safety" ? (
            <>
              <dl>
                <MetricStat
                  label="Safe to block"
                  value={percent(safety?.ratio)}
                  detail={`${count(safety?.safe)} / ${count(safety?.detected)} detected ${targets}`}
                />
              </dl>
              {safety?.ratio != null && (
                <div
                  className="h-2 overflow-hidden rounded bg-border"
                  role="img"
                  aria-label={`Safe to block ${percent(safety.ratio)}; Not confirmed safe to block ${percent(1 - safety.ratio)}`}
                >
                  <div
                    className="h-full bg-classification-safe"
                    style={{ width: `${safety.ratio * 100}%` }}
                  />
                </div>
              )}
              <p className="text-xs text-secondary">
                Not confirmed safe to block:{" "}
                <span className="tabular-nums">{count(safety?.notConfirmedSafe)}</span>
              </p>
              <details className="text-xs text-secondary">
                <summary className="cursor-pointer">Safety evidence</summary>
                <dl className="mt-2 grid grid-cols-2 gap-2">
                  {(
                    [
                      ["Safe-only", safety?.safeOnly],
                      ["Unsafe-only", safety?.unsafeOnly],
                      ["Conflicting", safety?.conflicting],
                      ["Unknown", safety?.unknown],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd className="tabular-nums">{count(value)}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            </>
          ) : mode === "volume" ? (
            <dl className="grid grid-cols-2 gap-4">
              <MetricStat
                label={`Detected ${targets}`}
                value={count(cohort?.detected)}
                detail="Distinct creation cohort"
              />
              <MetricStat
                label={`Sent ${targets}`}
                value={count(cohort?.sent)}
                detail="Populated sending map"
              />
            </dl>
          ) : (
            <dl>
              <MetricStat
                label="Effectiveness"
                value={percent(sending?.ratio)}
                detail={`${count(sending?.blockedSendings)} / ${count(sending?.eligibleSendings)} approved ticket/ISP sendings`}
              />
            </dl>
          )}
        </div>
      )}
    </Widget>
  );
}
const widgetModule: WidgetModule<IpBlockingSummaryConfig> = { manifest, Component, defaultConfig };
export default widgetModule;
